import React, { useState } from "react";

import { Flex, Icon, Link, Typography } from "components";

export type AlertBannerProps = {
  className?: string;
  closeable?: boolean;
  dataTestId?: string;
  description?: string | React.ReactNode;
  iconName?: string;
  iconSize?: number;
  iconAlign?: "start" | "center" | "end";
  title?: string;
  isTitleBold?: boolean;
  headingSize?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "paragraph-xs" | "paragraph-s" | "paragraph-m" | "paragraph-l" | "paragraph-xl";
  descriptionSize?: "paragraph-xs" | "paragraph-s" | "paragraph-m" | "paragraph-l" | "paragraph-xl";
  descriptionColor?: "grey-600" | "green-400" | "red-500" | "white";
  gap?: number;
  type?: "recommended" | "warning" | "info" | "info-highlighted" | "error" | "success" | string;
  link?: boolean;
  linkText?: string;
  linkType?: "primary-link" | "secondary-link" | "tertiary-link";
  linkAction?: React.ReactNode;
  onClick?: () => void;
  onClose?: () => void;
};

export const AlertBanner = ({
  className,
  closeable = false,
  dataTestId,
  description,
  iconName,
  iconSize = 32,
  iconAlign = "center",
  title,
  isTitleBold = false,
  headingSize = "h4",
  descriptionSize = "paragraph-m",
  descriptionColor,
  gap = 16,
  type = "normal",
  link,
  linkText,
  linkType,
  linkAction,
  onClick = () => {},
  onClose = () => {},
}: AlertBannerProps) => {
  const [hidden, setHidden] = useState(false);
  const isHexColor = type?.startsWith("#");

  const handleClose = () => {
    setHidden(true);
    onClose();
  };

  const handleButtonClick = () => {
    onClick();
  };

  const linkElement = link && (
    <Link onClick={handleButtonClick} {...(linkType ? { type: linkType } : {})}>
      {linkText}
    </Link>
  );

  return hidden ? null : (
    <div className={`alert-banner ${type} ${className ?? ""}`} data-testid={dataTestId} style={isHexColor ? { backgroundColor: type } : undefined}>
      <Flex className="width-100" gap={8} align={iconAlign}>
        {iconName && <Icon className="banner-icon flex-0" iconName={iconName} size={iconSize} />}
        <Flex className="width-100" vertical gap={gap}>
          {title && (
            <Typography variant={headingSize} bold={isTitleBold}>
              {title}
            </Typography>
          )}
          {description && typeof description === "string" ? (
            <Typography variant={descriptionSize} color={descriptionColor}>
              {description}
            </Typography>
          ) : (
            <>{description}</>
          )}
          {linkAction ? (
            <Flex className="link-row" align="center" justify="space-between" gap={16}>
              {linkElement}
              {linkAction}
            </Flex>
          ) : (
            linkElement
          )}
        </Flex>
      </Flex>
      {closeable && <Icon className="pointer" iconName="tabler/x" onClick={handleClose} />}
    </div>
  );
};
